import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Organization } from "../types/organization";
import {
  getOrganizationBySlug,
  getOrganizationByCustomDomain,
} from "../services/organizations";

interface OrganizationContextType {
  currentOrg: Organization | null;
  setCurrentOrg: (org: Organization | null) => void;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  refreshOrganization: () => Promise<void>;
  updateOrganization: (
    orgId: string,
    updates: Partial<Organization>,
  ) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrganizationFromURL();
  }, []);

  const loadOrganizationFromURL = async () => {
    const hostname = window.location.hostname;
    
    try {
      setLoading(true);
      setError(null);

      let org: Organization | null = null;

      // Development mode: localhost
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        // In development, check localStorage for selected org or use default
        const storedOrgSlug = localStorage.getItem("dev_org_slug");
        if (storedOrgSlug) {
          org = await getOrganizationBySlug(storedOrgSlug);
        } else {
          // Try to load a default organization for development
          org = await getOrganizationBySlug("default");
        }
      }
      // Production: subdomain routing (e.g., berlin.yourapp.com)
      else if (
        hostname.includes("yourapp.com") ||
        hostname.includes("netlify.app")
      ) {
        const subdomain = hostname.split(".")[0];
        if (subdomain && subdomain !== "www" && subdomain !== "yourapp") {
          org = await getOrganizationBySlug(subdomain);
        }
        // If slug lookup failed, the full hostname may be a custom domain
        // (e.g., stuttgart-padel-scores.netlify.app stored in branding.customDomain)
        if (!org) {
          org = await getOrganizationByCustomDomain(hostname);
        }
      }
      // Custom domain (e.g., berlinpadel.com)
      else {
        org = await getOrganizationByCustomDomain(hostname);
      }

      if (org) {
        setCurrentOrg(org);
        // Store the full organization object for API requests
        localStorage.setItem('currentOrganization', JSON.stringify(org));
      } else {
        // In development, allow the app to run without an organization
        // This lets you test before creating organizations in Firestore
        if (hostname === "localhost" || hostname === "127.0.0.1") {
          console.warn(
            "⚠️ No organization found. Run 'node server/src/scripts/create-test-organizations.js' to create test organizations."
          );
          setError(null); // Don't block the app in development
          setCurrentOrg(null);
        } else {
          setError("Organization not found. Please check the URL.");
        }
      }
    } catch (err) {
      console.error("Error loading organization:", err);
      // In development, allow the app to continue even if there's an error
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        console.warn("⚠️ Error loading organization, but continuing in dev mode");
        setError(null);
        setCurrentOrg(null);
      } else {
        setError("Failed to load organization");
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshOrganization = async () => {
    if (currentOrg) {
      try {
        const updated = await getOrganizationBySlug(currentOrg.slug);
        if (updated) {
          setCurrentOrg(updated);
        }
      } catch (err) {
        console.error("Error refreshing organization:", err);
      }
    }
  };

  const updateOrganization = async (
    orgId: string,
    updates: Partial<Organization>,
  ) => {
    try {
      // This will be implemented in the service layer
      // For now, just update locally
      if (currentOrg?.id === orgId) {
        setCurrentOrg({ ...currentOrg, ...updates });
      }
    } catch (err) {
      console.error("Error updating organization:", err);
      throw err;
    }
  };

  // Padel Eagle CI 2027 colors always win over whatever branding is stored in the database.
  const effectiveOrg: Organization | null =
    currentOrg?.slug === "padel-eagle"
      ? {
          ...currentOrg,
          branding: {
            ...currentOrg.branding,
            primaryColor: "#743444",
            secondaryColor: "#E49421",
            accentColor: "#E49421",
          },
        }
      : currentOrg;

  const value: OrganizationContextType = {
    currentOrg: effectiveOrg,
    setCurrentOrg,
    organizations,
    loading,
    error,
    refreshOrganization,
    updateOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
};

// Helper hook for development: switch between orgs
export const useDevOrgSwitcher = () => {
  const { setCurrentOrg } = useOrganization();

  const switchToOrg = async (slug: string) => {
    if (process.env.NODE_ENV === "development") {
      localStorage.setItem("dev_org_slug", slug);
      const org = await getOrganizationBySlug(slug);
      if (org) {
        setCurrentOrg(org);
        // Store the full organization object for API requests
        localStorage.setItem('currentOrganization', JSON.stringify(org));
        console.log(`[Organization] Switched to: ${org.name} (${org.id})`);
        // Force reload to refetch all data with new org context
        window.location.reload();
      }
    }
  };

  return { switchToOrg };
};
